<script setup lang="ts">
  import { ref, reactive, onMounted, computed, nextTick } from 'vue'
  import { Eye, Pencil, Check, X, Minus } from 'lucide-vue-next'
  import { Button } from '@/components/ui/button'
  import { Input } from '@/components/ui/input'
  import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
  } from '@/components/ui/table'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import { CasesService } from '@/types/generated/services.gen'
  import type { Document, CustomerCase } from '@/types/generated/types.gen'

  const props = defineProps<{
    caseData: CustomerCase
  }>()

  const emit = defineEmits(['updateDocuments', 'update:fileData'])

  interface ExportDocument extends Document {
    isSelected: boolean
    isEditing: boolean
    newName: string
  }

  const documents = ref<ExportDocument[]>([])
  const isLoading = ref(true)
  const error = ref<string | null>(null)
  const updateTrigger = ref(0)

  onMounted(async () => {
    documents.value = props.caseData.documents.map((doc) => ({
      ...doc,
      isSelected: true,
      isEditing: false,
      newName: doc.filename.replace('.pdf', '')
    }))
    emitFileData()
  })

  const selectedCount = computed(() => documents.value.filter((doc) => doc.isSelected).length)

  const toggleSelection = (doc: ExportDocument, isSelected: boolean) => {
    doc.isSelected = isSelected
    emitFileData()
  }

  const deselectAll = () => {
    documents.value.forEach((doc) => {
      doc.isSelected = false
    })
    updateTrigger.value += 1
    nextTick(() => {
      emitFileData()
    })
  }

  const viewDocument = (doc: ExportDocument) => {
    window.open(doc.url, '_blank')
  }

  const editDocument = (doc: ExportDocument) => {
    doc.isEditing = true
    doc.newName = doc.filename.replace('.pdf', '')
  }

  const saveDocumentName = (doc: ExportDocument) => {
    doc.filename = `${doc.newName}.pdf`
    doc.isEditing = false
    emitFileData()
  }

  const cancelEdit = (doc: ExportDocument) => {
    doc.isEditing = false
    doc.newName = doc.filename.replace('.pdf', '')
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
  }

  const fileData = computed(() => {
    return documents.value
      .filter((doc) => doc.isSelected)
      .map((doc) => ({
        uuid: doc.uuid,
        filename: doc.filename
      }))
  })

  const emitFileData = () => {
    emit('update:fileData', fileData.value)
    emit(
      'updateDocuments',
      documents.value.filter((doc) => doc.isSelected)
    )
  }

  const getIsSelected = (doc: ExportDocument) => ({
    get: () => doc.isSelected,
    set: (value: boolean) => {
      doc.isSelected = value
      emitFileData()
    }
  })
</script>

<template>
  <div class="rounded-xl border bg-white p-6 shadow-md">
    <h2 class="mb-4 text-2xl font-bold text-gray-900">Dokumente ({{ selectedCount }})</h2>
    <p class="mb-4 text-gray-600">
      Wählen Sie aus, welche Dokumente Sie zur Aktenanlage verwenden möchten.
    </p>
    <Table>
      <TableHeader>
        <TableRow class="bg-slate-50">
          <TableHead>
            <Button
              v-if="selectedCount > 0"
              variant="outline"
              size="sm"
              class="h-6 w-6 border border-purple-500 bg-purple-500 p-0"
              @click="deselectAll"
            >
              <Minus class="h-4 w-4 text-white" />
            </Button>
          </TableHead>
          <TableHead>Dokument Name</TableHead>
          <TableHead>Ursprung</TableHead>
          <TableHead>Erstellungsdatum</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <TableRow
          v-for="doc in documents"
          :key="`${doc.uuid}-${updateTrigger}`"
          class="hover:bg-slate-50"
        >
          <TableCell class="w-12">
            <CheckboxField
              v-model="doc.isSelected"
              :name="`document-${doc.uuid}`"
              :checkedValue="true"
              :uncheckedValue="false"
              @update:model-value="(isSelected: boolean) => toggleSelection(doc, isSelected)"
            />
          </TableCell>
          <TableCell class="p-2">
            <div class="flex items-center justify-between">
              <div class="flex-grow">
                <template v-if="doc.isEditing">
                  <div class="flex items-center">
                    <Input
                      v-model="doc.newName"
                      class="w-full"
                    />
                    <span class="ml-2 text-gray-500">.pdf</span>
                  </div>
                </template>
                <template v-else>
                  <span>{{ doc.filename.replace('.pdf', '') }}</span>
                  <span class="text-gray-500">.pdf</span>
                </template>
              </div>
              <div class="ml-2 flex items-center space-x-2">
                <template v-if="doc.isEditing">
                  <Button
                    variant="outline"
                    size="sm"
                    class="h-8 w-8 border border-slate-300 p-0"
                    @click="saveDocumentName(doc)"
                  >
                    <Check class="h-4 w-4 text-green-500" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    class="h-8 w-8 border border-slate-300 p-0"
                    @click="cancelEdit(doc)"
                  >
                    <X class="h-4 w-4 text-red-500" />
                  </Button>
                </template>
                <template v-else>
                  <Button
                    variant="outline"
                    size="sm"
                    class="h-8 w-8 border border-slate-300 p-0"
                    @click="viewDocument(doc)"
                  >
                    <Eye class="h-4 w-4 text-slate-500" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    class="h-8 w-8 border border-slate-300 p-0"
                    @click="editDocument(doc)"
                  >
                    <Pencil class="h-4 w-4 text-slate-500" />
                  </Button>
                </template>
              </div>
            </div>
          </TableCell>
          <TableCell class="p-2">
            {{ doc.document_type || 'Unknown' }}
          </TableCell>
          <TableCell class="p-2">
            {{ formatDate(doc.created_at) }}
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  </div>
</template>
